import { isActiveProduct, type CheckoutPaymentSchema } from '$lib/api/stripe';
import { type InferInput } from 'valibot';
import type { GTMDataLayerData } from './gtm';
import { inDollars } from './money';
import { getImpactClickId } from './tracking/impact';
import type { Stripe } from 'stripe';
import type { CheckoutSessionData } from '$lib/state/checkoutSessionStorage';
import { dev } from '$app/environment';
import log from './log';
import type { FinancingOption } from '$lib/storyblok/types';
import { hashEmail } from './email';

function getOrderAmountFromSession(session: Stripe.Checkout.Session): number {
	const amount = (session.amount_subtotal ?? 0) - (session.total_details?.amount_discount ?? 0);

	return inDollars(amount);
}

export interface TrackableItem {
	product_id: string;
	sku: string;
	name: string;
	price: number;
	quantity: number | null;
	// Non-standard event property.
	plan_interval: Stripe.Price.Recurring.Interval | undefined;
	price_lookup_key: string | null | undefined;
}

// matches the same logic as:
// https://github.com/chewielabs/ChewieWebAccount/blob/main/www/src/utils/segment.js
export function convertToTrackableItem(item: Stripe.LineItem): TrackableItem {
	const product = item?.price?.product as Stripe.Product;

	return {
		product_id: product?.id ?? '-',
		sku: item.price?.metadata._product_sku ?? '-',
		name: product?.name ?? '-',
		price: inDollars(item.price?.unit_amount ?? 0),
		quantity: item.quantity,
		plan_interval: item.price?.recurring?.interval,
		price_lookup_key: item.price?.lookup_key
	};
}

// use sub-total - discount, based on
// https://github.com/chewielabs/ChewieWebAccount/blob/stripe-migration/amplify/backend/function/chewieUM/src/services/segment-service.js#L65
export async function getDataLayerData(session: Stripe.Checkout.Session): Promise<DeepPartial<GTMDataLayerData>> {
	return {
		order: {
			total: getOrderAmountFromSession(session),
			id: session.id,
			email: session.customer_details?.email ?? '',
			hashed_email: session.customer_details?.email ? await hashEmail(session.customer_details?.email) : '',
			currency: session.currency?.toUpperCase() ?? 'USD',
			couponCode: session.total_details?.breakdown?.discounts?.[0]?.discount.coupon.name ?? '',
			quantity:
				session.line_items?.data?.reduce((carr, i) => {
					return carr + (i.quantity || 1);
				}, 0) || 1,
			type: session.mode === 'subscription' ? 'rental' : 'purchase'
		}
	};
}

function getPaymentMethod(checkoutData: CheckoutSessionData): FinancingOption {
	if (checkoutData.product?.price.type !== 'one_time') return 'card_only';

	return checkoutData.financingOptions ?? 'all';
}

export async function getStripeCheckoutSession(
	checkoutData: CheckoutSessionData,
	signal?: AbortSignal
): Promise<Stripe.Response<Stripe.Checkout.Session>> {
	if (!checkoutData.product?.price || !isActiveProduct(checkoutData.product.price.product)) {
		throw new Error('Cannot proceed with checkout: Invalid Product');
	}

	if (dev) {
		log.scope('Stripe').debug('Generating checkout session', {
			product_id: checkoutData.product.price.product.id,
			promo_id: checkoutData.product.promotionCode?.id,
			lookup_key: checkoutData.product.price.lookup_key
		});
	}

	const response = await fetch('/api/stripe/checkout', {
		signal,
		method: 'POST',
		body: JSON.stringify({
			meta: {
				userAgent: navigator.userAgent,
				termsOfService: checkoutData.termsOfService,
				afterSubmitText: checkoutData.afterSubmitText,
				successPage: checkoutData.successPage,
				productSubline: checkoutData.productSubline
			},
			productId: checkoutData.product.price.product.id,
			priceId: checkoutData.product.price.id,
			interval: checkoutData.product.price.recurring?.interval,
			isTrial: checkoutData.isTrial,
			allowedPaymentMethods: getPaymentMethod(checkoutData),
			accessCode: checkoutData.accessCode,
			userLabels: Array.from(checkoutData.userLabels || []),
			impactClickId: await getImpactClickId(),
			shippingId: checkoutData.shippingId,
			etaWeeks: checkoutData.etas,
			adjustableQuantity: checkoutData.adjustableQuantity
		} satisfies InferInput<typeof CheckoutPaymentSchema>)
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error?.message ?? 'Something went wrong. Please try again.');
	}

	return (await response.json()) as Stripe.Response<Stripe.Checkout.Session>;
}

export function getTrackingAmounts(session: Stripe.Checkout.Session) {
	const discount = session.total_details?.amount_discount ?? 0;
	const subtotalBeforeDiscountsAndTaxes = session.amount_subtotal ?? 0;
	const subtotalAfterDiscountsAndBeforeTaxes = subtotalBeforeDiscountsAndTaxes - discount;

	const cents = {
		discount,
		subtotalBeforeDiscountsAndTaxes,
		subtotalAfterDiscountsAndBeforeTaxes,
		shipping: session.total_details?.amount_shipping ?? 0,
		tax: session.total_details?.amount_tax ?? 0,
		total: session.amount_total ?? 0
	};

	return {
		cents,
		dollars: {
			discount: inDollars(cents.discount),
			shipping: inDollars(cents.shipping),
			subtotalBeforeDiscountsAndTaxes: inDollars(cents.subtotalBeforeDiscountsAndTaxes),
			subtotalAfterDiscountsAndBeforeTaxes: inDollars(cents.subtotalAfterDiscountsAndBeforeTaxes),
			tax: inDollars(cents.tax),
			total: inDollars(cents.total)
		}
	};
}
